/* eslint-disable max-lines */
import { captureError } from '@smg-real-estate/browser-rum';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { ApiResponseError } from '@/api/ApiResponseError';
import {
  deleteInsertion as callDeleteInsertion,
  deleteDeactivationSchedule,
  getInsertions,
  getPricingParameters,
  postDeactivationSchedule,
  postInsertionArchive,
  postInsertionDuplication,
  postInsertionReactivate,
  saveCustomerSegment,
} from '@/api/insertions';
import {
  getListingPerformanceStats,
  type ListingPerformanceStats,
} from '@/api/listingPerformanceStats';
import { getBundlePrices, type PricingApiBundle, type PricingParameters } from '@/api/pricing';
import { useImpersonation } from '@/composables/useImpersonation';
import { isMultiWeeksInsertion } from '@/helpers/pricingUtils';
import { UPGRADE_LISTING_CONVERSION_SOURCE } from '@/services/tracking/Tracking';
import {
  DeactivationScheduleTargetTerm,
  InsertionStatus,
  ScheduledDeactivationReason,
  type DeactivationReasonKey,
  type DeactivationSchedule,
  type Insertion,
  type SelectedBundle,
  type TenantDocMeta,
} from '@/types';

enum ApiCallStatus {
  Ready = 'READY',
  InProgress = 'IN_PROGRESS',
  Success = 'SUCCESS',
  Error = 'ERROR',
}

interface ArchiveInsertionContext {
  insertionId: string;
  targetTerm?: DeactivationScheduleTargetTerm;
}

interface DeleteInsertionContext {
  insertionId: string;
  objectReference: string;
  status: InsertionStatus;
}

interface DeactivationScheduleContext {
  insertionId: string;
}

interface CancelDeactivationScheduleContext {
  insertionId: string;
}

interface ViewingAppointmentBundleUpgradePromptContext {
  insertionId: string;
}

interface UpgradeBundleContext {
  insertionId: string;
  conversionSource: string;
}

interface ContactSeekersUpgradeContext {
  insertionId: string;
}

interface T2FContractUploadContext {
  insertionId: string;
}

// eslint-disable-next-line max-statements
export const useMyAdsStore = defineStore('myAds', () => {
  const { ownerId } = useImpersonation();

  const insertions = ref<Insertion[]>([]);
  const fetchStatus = ref(ApiCallStatus.Ready);
  const isFetching = computed(() => fetchStatus.value === ApiCallStatus.InProgress);
  const hasFetchError = computed(() => fetchStatus.value === ApiCallStatus.Error);
  const hasInitialFetchCompleted = ref(false);
  const nextPageToFetch = ref<string>();
  const hasNextPage = computed(() => !!nextPageToFetch.value);
  const isEmpty = computed(() => insertions.value.length === 0);

  const insertionsWithOngoingAction = ref(new Set<string>());
  /**
   * This indicates if a blocking action, such as duplicating an insertion, is ongoing.
   * Since the user will be redirected after the action succeeds, the UI should be blocked
   * during this time to prevent multiple actions from being triggered.
   */
  const isBlockingActionOngoing = ref(false);

  function getInsertionById(insertionId?: string) {
    if (!insertionId) return undefined;
    return insertions.value.find((insertion) => insertion.id === insertionId);
  }

  /**
   * Insertions are paginated, so an insertion referenced by a deep link
   * (e.g. `?listingId=...`) may be on a page that has not been fetched yet.
   * This keeps fetching subsequent pages until the insertion is found or there
   * are no more pages, and resolves with the insertion (or `undefined`).
   */
  async function ensureInsertionLoaded(insertionId?: string): Promise<Insertion | undefined> {
    if (!insertionId) return undefined;

    let insertion = getInsertionById(insertionId);
    while (!insertion && hasNextPage.value) {
      await fetchInsertions();
      if (hasFetchError.value) break;
      insertion = getInsertionById(insertionId);
    }
    return insertion;
  }

  function removeInsertionFromList(insertionId: string) {
    insertions.value = insertions.value.filter((i) => i.id !== insertionId);
  }

  function updateInsertionStatus(insertionId: string, status: InsertionStatus) {
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return;
    }

    insertion.status = status;
  }

  function updateInsertionDeactivationSchedule(
    insertionId: string,
    deactivationSchedule: DeactivationSchedule,
  ) {
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return;
    }

    insertion.deactivationSchedule = deactivationSchedule;
  }

  function updateInsertionSelectedBundle(insertionId: string, selectedBundle: SelectedBundle) {
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return;
    }

    insertion.selectedBundle = selectedBundle;
  }

  async function fetchInsertions() {
    try {
      fetchStatus.value = ApiCallStatus.InProgress;
      const response = await getInsertions(ownerId.value, nextPageToFetch.value);
      insertions.value.push(...response.data);
      nextPageToFetch.value = response.cursors?.next;
      hasInitialFetchCompleted.value = true;
      fetchStatus.value = ApiCallStatus.Success;
    } catch {
      toastType.value = 'error';
      fetchStatus.value = ApiCallStatus.Error;
    }
  }

  async function reactivateInsertion(insertionId: string) {
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return;
    }

    try {
      insertionsWithOngoingAction.value.add(insertionId);
      const reactivatedInsertion = await postInsertionReactivate(insertion.userId, insertionId);
      if (reactivatedInsertion.status === InsertionStatus.Draft) {
        await saveCustomerSegment(
          insertion.userId,
          reactivatedInsertion.id,
          insertion.customerSegment,
        );
      }
      removeInsertionFromList(insertionId);
      insertions.value.unshift(reactivatedInsertion);
      if (
        isMultiWeeksInsertion(reactivatedInsertion.selectedBundle?.period) &&
        reactivatedInsertion.status === InsertionStatus.Republishing
      ) {
        toastSuccessTitle.value = 'myAdsPage.toasts.insertionWillBeReactivated';
        toastSuccessMessage.value = 'myAdsPage.toasts.insertionWillBeReactivatedDescription';
      }
      toastType.value = 'success';
    } catch (error) {
      const isUnpaidInvoicesError = error instanceof ApiResponseError && error.statusCode === 402;
      if (isUnpaidInvoicesError) {
        toastErrorMessage.value = 'billingPage.paymentMethod.error.unpaidInvoices';
      }
      toastType.value = 'error';
    } finally {
      insertionsWithOngoingAction.value.delete(insertionId);
    }
  }

  const deactivationScheduleContext = ref<DeactivationScheduleContext>();
  const archiveInsertionContext = ref<ArchiveInsertionContext>();

  async function scheduleDeactivation(
    archivalReasonKey: ScheduledDeactivationReason,
    archivalReasonText: string,
  ): Promise<boolean> {
    const { targetTerm, insertionId } = archiveInsertionContext.value ?? {};
    if (!insertionId || !targetTerm) return false;
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return false;
    }

    try {
      insertionsWithOngoingAction.value.add(insertionId);
      const archivalReason = {
        key: archivalReasonKey,
        reason: archivalReasonText,
      };
      const deactivationSchedule = await postDeactivationSchedule(
        insertion.userId,
        insertion.id,
        archivalReason,
        targetTerm,
      );
      updateInsertionDeactivationSchedule(insertionId, deactivationSchedule);
      toastSuccessTitle.value = 'myAdsPage.toasts.successTitle';
      toastType.value = 'success';
      return true;
    } catch {
      toastType.value = 'error';
      return false;
    } finally {
      insertionsWithOngoingAction.value.delete(insertionId);
      archiveInsertionContext.value = undefined;
    }
  }

  async function archiveInsertion(
    archivalReasonKey: DeactivationReasonKey,
    archivalReasonText: string,
  ): Promise<boolean> {
    const insertionId = archiveInsertionContext.value?.insertionId;
    if (!insertionId) return false;
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return false;
    }

    try {
      insertionsWithOngoingAction.value.add(insertionId);
      await postInsertionArchive(insertion.userId, insertionId, {
        key: archivalReasonKey,
        reason: archivalReasonText,
      });
      updateInsertionStatus(insertionId, InsertionStatus.Archiving);
      toastSuccessTitle.value = 'myAdsPage.toasts.insertionWillBeDeactivated';
      toastSuccessMessage.value = 'myAdsPage.toasts.confirmationEmailNotice';
      toastType.value = 'success';
      return true;
    } catch {
      toastType.value = 'error';
      return false;
    } finally {
      insertionsWithOngoingAction.value.delete(insertionId);
      archiveInsertionContext.value = undefined;
    }
  }

  const cancelDeactivationScheduleContext = ref<CancelDeactivationScheduleContext>();
  const upgradeBundleContext = ref<UpgradeBundleContext>();
  const contactSeekersUpgradeContext = ref<ContactSeekersUpgradeContext>();
  const viewingAppointmentBundleUpgradePromptContext =
    ref<ViewingAppointmentBundleUpgradePromptContext>();
  const t2fContractUploadContext = ref<T2FContractUploadContext>();

  async function cancelDeactivationSchedule() {
    const insertionId = cancelDeactivationScheduleContext.value?.insertionId;
    if (!insertionId) return;

    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return;
    }

    try {
      insertionsWithOngoingAction.value.add(insertionId);
      await deleteDeactivationSchedule(insertion.userId, insertionId);

      delete insertion.deactivationSchedule;

      toastType.value = 'success';
    } catch {
      toastType.value = 'error';
    } finally {
      insertionsWithOngoingAction.value.delete(insertionId);
      cancelDeactivationScheduleContext.value = undefined;
    }
  }

  const deleteInsertionContext = ref<DeleteInsertionContext>();
  async function deleteInsertion() {
    const insertionId = deleteInsertionContext.value?.insertionId;
    if (!insertionId) return;
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return;
    }

    try {
      insertionsWithOngoingAction.value.add(insertionId);
      await callDeleteInsertion(insertion.userId, insertionId);
      removeInsertionFromList(insertionId);
      toastSuccessMessage.value = 'myAdsPage.toasts.insertionSuccessfullyDeleted';
      toastType.value = 'success';
    } catch {
      toastType.value = 'error';
    } finally {
      insertionsWithOngoingAction.value.delete(insertionId);
    }
  }

  async function duplicateInsertion(insertionId: string) {
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return;
    }

    try {
      isBlockingActionOngoing.value = true;
      insertionsWithOngoingAction.value.add(insertionId);
      const duplicatedInsertion = await postInsertionDuplication(insertion.userId, insertionId);
      await saveCustomerSegment(
        insertion.userId,
        duplicatedInsertion.id,
        insertion.customerSegment,
      );
      return duplicatedInsertion.id;
    } catch {
      toastType.value = 'error';
    } finally {
      insertionsWithOngoingAction.value.delete(insertionId);
      isBlockingActionOngoing.value = false;
    }
  }

  const listingsPerformanceStats = ref<Record<string, ListingPerformanceStats | undefined>>({});
  const fetchingListingPerformanceStatsIds = ref(new Set<string>());

  async function fetchListingPerformanceStats() {
    if (!insertions.value.length) {
      return;
    }

    const insertionsMissingPerformanceStats = insertions.value
      .filter((insertion) => insertion.firstPublishedDate)
      .filter((i) => !Object.hasOwn(listingsPerformanceStats.value, i.objectReference))
      .filter((i) => !fetchingListingPerformanceStatsIds.value.has(i.objectReference))
      .map((x: Insertion) => {
        return { insertionId: x.id, objectReference: x.objectReference };
      });

    if (insertionsMissingPerformanceStats.length === 0) {
      return;
    }

    insertionsMissingPerformanceStats.forEach((insertion) => {
      fetchingListingPerformanceStatsIds.value.add(insertion.objectReference);
    });

    try {
      const fetchedPerformanceStats = await getListingPerformanceStats(
        ownerId.value,
        insertionsMissingPerformanceStats.map((i) => i.insertionId),
      );

      insertionsMissingPerformanceStats.forEach((insertion) => {
        const performanceStats = fetchedPerformanceStats.find(
          (stats) => stats.objectReference === insertion.objectReference,
        );
        listingsPerformanceStats.value[insertion.objectReference] = performanceStats;
      });
    } catch {
      // Errors are captured in the API function. Record attempted listings so charts
      // leave Idle instead of treating a failed fetch as still loading.
      insertionsMissingPerformanceStats.forEach((insertion) => {
        listingsPerformanceStats.value[insertion.objectReference] = undefined;
      });
    } finally {
      insertionsMissingPerformanceStats.forEach((insertion) => {
        fetchingListingPerformanceStatsIds.value.delete(insertion.objectReference);
      });
    }
  }

  const insertionBundles = ref<Record<string, PricingApiBundle[]>>({});
  const insertionPricingParams = ref<Record<string, PricingParameters>>({});
  const fetchingBundleIds = ref(new Set<string>());
  const isFetchingUpgradeBundles = computed(() => fetchingBundleIds.value.size > 0);

  function setBundlesForInsertion(insertionId: string, bundles: PricingApiBundle[]) {
    insertionBundles.value[insertionId] = bundles;
  }

  function getBundlesForInsertion(insertionId: string): PricingApiBundle[] {
    return insertionBundles.value[insertionId] || [];
  }

  function getCustomerSegmentForInsertion(insertionId: string) {
    return insertionPricingParams.value[insertionId]?.customerSegment;
  }

  async function fetchBundlesForInsertion(insertion: Insertion) {
    if (insertionBundles.value[insertion.id]?.length > 0) {
      return;
    }

    fetchingBundleIds.value.add(insertion.id);
    try {
      const pricingParams = await getPricingParameters(insertion.userId, insertion.id);
      insertionPricingParams.value[insertion.id] = pricingParams;

      const { data } = await getBundlePrices(pricingParams);

      setBundlesForInsertion(insertion.id, [...data.bundles, ...(data.offer?.crosslisting || [])]);
    } catch {
      // Errors are captured in the API function, so we can fail silently here
    } finally {
      fetchingBundleIds.value.delete(insertion.id);
    }
  }

  async function openUpgradeBundleModal(
    insertion: Insertion,
    options?: { conversionSource?: string },
  ): Promise<boolean> {
    await fetchBundlesForInsertion(insertion);

    const hasBundles = getBundlesForInsertion(insertion.id).length > 0;
    if (hasBundles) {
      upgradeBundleContext.value = {
        insertionId: insertion.id,
        conversionSource: options?.conversionSource ?? UPGRADE_LISTING_CONVERSION_SOURCE,
      };
      return true;
    }

    toastErrorMessage.value = 'myAdsPage.toasts.bundleUpgradeFailed';
    toastType.value = 'error';
    return false;
  }

  function updateInsertionTenantDoc(insertionId: string, tenantDoc: TenantDocMeta) {
    const insertion = insertions.value.find((i) => i.id === insertionId);
    if (!insertion) {
      captureError(new Error('Insertion not found in myAds store'), { insertionId });
      return;
    }

    insertion.meta = { ...insertion.meta, tenantDoc };
  }

  const toastType = ref<'success' | 'error'>();
  const toastSuccessTitle = ref<string>('myAdsPage.toasts.successTitle');
  const toastSuccessMessage = ref<string>('');
  const toastErrorMessage = ref<string>('');
  const shouldShowToast = computed(() => !!toastType.value);

  function resetToast() {
    toastType.value = undefined;
    toastSuccessTitle.value = 'myAdsPage.toasts.successTitle';
    toastSuccessMessage.value = '';
    toastErrorMessage.value = '';
  }

  /* eslint-disable max-statements */
  function $reset() {
    insertions.value = [];
    fetchStatus.value = ApiCallStatus.Ready;
    hasInitialFetchCompleted.value = false;
    nextPageToFetch.value = undefined;
    insertionsWithOngoingAction.value = new Set<string>();
    isBlockingActionOngoing.value = false;
    archiveInsertionContext.value = undefined;
    deleteInsertionContext.value = undefined;
    deactivationScheduleContext.value = undefined;
    upgradeBundleContext.value = undefined;
    t2fContractUploadContext.value = undefined;
    contactSeekersUpgradeContext.value = undefined;
    viewingAppointmentBundleUpgradePromptContext.value = undefined;
    toastType.value = undefined;
    toastSuccessTitle.value = 'myAdsPage.toasts.successTitle';
    toastSuccessMessage.value = '';
    toastErrorMessage.value = '';
    listingsPerformanceStats.value = {};
    fetchingListingPerformanceStatsIds.value.clear();
    insertionBundles.value = {};
    insertionPricingParams.value = {};
    fetchingBundleIds.value.clear();
  }

  return {
    insertions,
    isEmpty,
    isFetching,
    hasFetchError,
    hasInitialFetchCompleted,
    hasNextPage,
    insertionsWithOngoingAction,
    isBlockingActionOngoing,

    fetchInsertions,
    getInsertionById,
    ensureInsertionLoaded,
    reactivateInsertion,
    deactivationScheduleContext,
    archiveInsertionContext,
    archiveInsertion,
    scheduleDeactivation,
    cancelDeactivationScheduleContext,
    cancelDeactivationSchedule,
    upgradeBundleContext,
    contactSeekersUpgradeContext,
    t2fContractUploadContext,
    viewingAppointmentBundleUpgradePromptContext,
    updateInsertionSelectedBundle,
    deleteInsertionContext,
    deleteInsertion,
    duplicateInsertion,
    updateInsertionTenantDoc,

    fetchListingPerformanceStats,
    fetchingListingPerformanceStatsIds,
    listingsPerformanceStats,

    insertionBundles,
    isFetchingUpgradeBundles,
    setBundlesForInsertion,
    getBundlesForInsertion,
    getCustomerSegmentForInsertion,
    fetchBundlesForInsertion,
    openUpgradeBundleModal,

    toastType,
    toastSuccessTitle,
    toastSuccessMessage,
    toastErrorMessage,
    shouldShowToast,
    resetToast,

    $reset,
  };
});
